import React, { useEffect, useState } from 'react';
import { Building2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const RELEVANT_STATUSES = new Set(['submitted', 'reviewing', 'on_hold', 'approved']);
const BANK_ACCOUNT_PROMPT_SKIPPED_KEY = 'bankAccountNumberPromptSkipped';

const hasBankAccountNumber = (financialInfo = {}) =>
  Boolean(String(financialInfo.account_number || financialInfo.bank_account_number || '').trim());

const BankAccountNumberPrompt = ({ onOpen, onDismiss }) => {
  const { isAuthenticated, member } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    const loadRegistration = async () => {
      if (!isAuthenticated || member?.has_admin_rights === false) {
        setShouldShow(false);
        return;
      }

      if (sessionStorage.getItem(BANK_ACCOUNT_PROMPT_SKIPPED_KEY) === 'true') {
        setShouldShow(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get('/api/company-registration/my-registration');
        const data = response.data || {};
        const status = String(data.status || '').trim().toLowerCase();
        const needsAccountNumber =
          data.exists &&
          data.can_manage_registration &&
          RELEVANT_STATUSES.has(status) &&
          !hasBankAccountNumber(data.financial_info);

        setRegistration(data.exists ? data : null);
        setShouldShow(Boolean(needsAccountNumber));
      } catch (error) {
        console.error('Failed to check bank account number requirement:', error);
        setShouldShow(false);
      } finally {
        setLoading(false);
      }
    };

    loadRegistration();
  }, [isAuthenticated, member?.has_admin_rights, member?.member_id]);

  useEffect(() => {
    if (shouldShow) {
      onOpen?.();
    }
  }, [onOpen, shouldShow]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedAccountNumber = accountNumber.trim();

    if (!trimmedAccountNumber) {
      toast.error('Bank account number is required');
      return;
    }

    setSaving(true);
    try {
      await api.patch('/api/company-registration/my-registration/bank-account-number', {
        account_number: trimmedAccountNumber,
      });
      setRegistration((prev) => ({
        ...(prev || {}),
        financial_info: {
          ...(prev?.financial_info || {}),
          account_number: trimmedAccountNumber,
        },
      }));
      setShouldShow(false);
      onDismiss?.();
      toast.success('Bank account number updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bank account number');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem(BANK_ACCOUNT_PROMPT_SKIPPED_KEY, 'true');
    setShouldShow(false);
    onDismiss?.();
  };

  const handleDialogOpenChange = (open) => {
    if (!open && !saving) {
      handleSkip();
    }
  };

  const preventDismissWhileSaving = (event) => {
    if (saving) {
      event.preventDefault();
    }
  };

  if (loading || !shouldShow) return null;

  return (
    <Dialog open={shouldShow} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="max-h-[90dvh] max-w-md overflow-y-auto"
        onEscapeKeyDown={preventDismissWhileSaving}
        onPointerDownOutside={preventDismissWhileSaving}
      >
        <DialogHeader className="text-left">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-blue-50 p-2 text-blue-600">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">Bank Account Number Required</DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                Add the bank account number for {registration?.company_info?.company_name || 'your company'} to complete the bank information on file.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <Label htmlFor="bank-account-number">Bank Account Number <span className="text-red-500">*</span></Label>
            <Input
              id="bank-account-number"
              value={accountNumber}
              onChange={(event) => setAccountNumber(event.target.value)}
              placeholder="Enter current bank account number"
              autoFocus
              disabled={saving}
            />
          </div>

          <Button type="submit" className="w-full" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Bank Account Number'
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleSkip}
            disabled={saving}
          >
            Skip for now
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BankAccountNumberPrompt;
